import { useState } from 'react';
import Icon from '../ui/Icon';
import BrandLogo from '../ui/BrandLogo';
import { httpImageUrl, validUntil } from './couponAdapter';

/**
 * How one level's coupon looks in the app's Rewards "Targets" card (locked), and
 * after unlocking (code + "Valid until …"). Pure UI, no API call.
 */
export default function CouponPreview({ level, scansRequired }) {
  const [unlocked, setUnlocked] = useState(false);
  const title = level.offerText.trim() || 'Offer text';
  const subtitle = level.offerSubtitle.trim();
  const code = level.couponCode.trim().toUpperCase();
  const days = /^\d+$/.test(String(level.validForDays).trim()) ? Number(level.validForDays) : null;
  const until = validUntil(days);

  return (
    <div className="rounded-xl bg-surface-container-low p-3 border border-outline-variant/30">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-on-surface-variant">App preview</span>
        <div role="radiogroup" aria-label="Preview state" className="flex p-0.5 rounded-md bg-surface-container text-[10px] font-semibold">
          {[
            ['locked', 'Target'],
            ['unlocked', 'Unlocked'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={(key === 'unlocked') === unlocked}
              onClick={() => setUnlocked(key === 'unlocked')}
              className={`px-2 py-0.5 rounded outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot ${
                (key === 'unlocked') === unlocked ? 'bg-surface-container-lowest text-primary shadow-xs' : 'text-on-surface-variant'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 rounded-2xl bg-surface-container-lowest p-3 shadow-xs">
        <BrandLogo url={httpImageUrl(level.logoUrl)} brand={level.brandName || '?'} size="lg" rounded="rounded-full" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-on-surface truncate">{title}</p>
          {subtitle && <p className="text-[11px] text-on-surface-variant truncate">{subtitle}</p>}
          {unlocked ? (
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
              {code ? (
                <span className="font-data-mono font-bold px-1.5 rounded border border-dashed border-secondary text-secondary">{code}</span>
              ) : (
                <span className="italic text-on-surface-variant">Code issued per user</span>
              )}
              <span className="text-on-surface-variant">{until ? `Valid until ${until}` : 'No expiry'}</span>
            </div>
          ) : null}
        </div>
        {unlocked ? (
          <Icon name="lock_open" size={18} className="text-secondary shrink-0" />
        ) : (
          scansRequired != null && (
            <span className="shrink-0 px-2 py-0.5 rounded-full bg-secondary-container/40 text-on-secondary-container text-[10px] font-bold whitespace-nowrap">
              {scansRequired} Scans
            </span>
          )
        )}
      </div>
    </div>
  );
}
